// Phase 9B — BoardItemCard.
//
// Core visual card for a single board item. Shows priority stripe,
// title, status pill, quantitative values, tags, meta row, and
// optional progress bar. Premium terminal intelligence aesthetic.
//
// Used across all 6 boards with board-specific rendering via
// the meta/value columns.

import type { BoardItem, BoardItemStatus, BoardPriority } from "../../boards/useBoardItems.ts";
import * as s from "./board.styles.ts";

// ─── Helpers ───────────────────────────────────────────────────

function fmtNum(n: number | null, decimals = 2): string {
  if (n === null || !Number.isFinite(n)) return "—";
  return n.toFixed(decimals);
}

function fmtPct(n: number | null): string {
  if (n === null || !Number.isFinite(n)) return "—";
  return `${n.toFixed(1)}%`;
}

function fmtDate(raw: string | null): string {
  if (raw === null || raw.length === 0) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  const day = d.getUTCDate().toString().padStart(2, "0");
  const mon = d.toLocaleString("en-US", { month: "short", timeZone: "UTC" });
  const hr = d.getUTCHours().toString().padStart(2, "0");
  const min = d.getUTCMinutes().toString().padStart(2, "0");
  return `${day} ${mon} ${hr}:${min}`;
}

function relativeTime(raw: string): string {
  const diff = Date.now() - new Date(raw).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${String(mins)}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${String(hrs)}h ago`;
  const days = Math.floor(hrs / 24);
  return `${String(days)}d ago`;
}

// ─── Priority helpers ──────────────────────────────────────────

const PRIORITY_STRIPE: Record<BoardPriority, typeof s.priorityCritical> = {
  critical: s.priorityCritical,
  high: s.priorityHigh,
  medium: s.priorityMedium,
  low: s.priorityLow,
  none: s.priorityNone,
};

const PRIORITY_TEXT: Record<BoardPriority, typeof s.priorityCriticalText> = {
  critical: s.priorityCriticalText,
  high: s.priorityHighText,
  medium: s.priorityMediumText,
  low: s.priorityLowText,
  none: s.priorityLowText,
};

const PRIORITY_LABELS: Record<BoardPriority, string> = {
  critical: "CRIT",
  high: "HIGH",
  medium: "MED",
  low: "LOW",
  none: "",
};

// ─── Status helpers ────────────────────────────────────────────

const STATUS_STYLE: Record<BoardItemStatus, typeof s.statusBase> = {
  detected: s.statusDetected,
  qualifying: s.statusQualifying,
  backlog: s.statusBacklog,
  queued: s.statusQueued,
  active: s.statusActive,
  in_review: s.statusInReview,
  approved: s.statusApproved,
  ready: s.statusReady,
  executing: s.statusExecuting,
  live: s.statusLive,
  monitoring: s.statusMonitoring,
  suspended: s.statusSuspended,
  resolved_win: s.statusResolvedWin,
  resolved_loss: s.statusResolvedLoss,
  resolved_void: s.statusResolvedVoid,
  archived: s.statusArchived,
  learned: s.statusLearned,
};

const STATUS_LABELS: Record<BoardItemStatus, string> = {
  detected: "Detected",
  qualifying: "Qualifying",
  backlog: "Backlog",
  queued: "Queued",
  active: "Active",
  in_review: "Review",
  approved: "Approved",
  ready: "Ready",
  executing: "Executing",
  live: "Live",
  monitoring: "Monitoring",
  suspended: "Suspended",
  resolved_win: "Win",
  resolved_loss: "Loss",
  resolved_void: "Void",
  archived: "Archived",
  learned: "Learned",
};

// ─── Lane dot color ────────────────────────────────────────────

export const STATUS_DOT_COLORS: Record<BoardItemStatus, string> = {
  detected: "rgba(168, 85, 247, 0.7)",
  qualifying: "var(--sz-info)",
  backlog: "var(--sz-text-muted)",
  queued: "var(--sz-text-secondary)",
  active: "var(--sz-success)",
  in_review: "var(--sz-amber)",
  approved: "var(--sz-success)",
  ready: "var(--sz-success)",
  executing: "var(--sz-amber)",
  live: "var(--sz-success)",
  monitoring: "var(--sz-info)",
  suspended: "var(--sz-warning)",
  resolved_win: "var(--sz-success)",
  resolved_loss: "var(--sz-danger)",
  resolved_void: "var(--sz-text-muted)",
  archived: "var(--sz-text-muted)",
  learned: "rgba(168, 85, 247, 0.5)",
};

// ─── Value block (board-specific) ──────────────────────────────

function ValueBlock({ item }: { item: BoardItem }): JSX.Element | null {
  if (item.value_primary === null && item.value_secondary === null && item.value_pct === null) {
    return null;
  }

  return (
    <div style={s.valueRow}>
      {item.value_primary !== null ? (
        <div style={s.valueCell}>
          <span style={s.valueNumber}>{fmtNum(item.value_primary)}</span>
          <span style={s.valueLabel}>Primary</span>
        </div>
      ) : null}
      {item.value_secondary !== null ? (
        <div style={s.valueCell}>
          <span style={s.valueNumber}>{fmtNum(item.value_secondary)}</span>
          <span style={s.valueLabel}>Secondary</span>
        </div>
      ) : null}
      {item.value_pct !== null ? (
        <div style={s.valueCell}>
          <span style={s.valueNumber}>{fmtPct(item.value_pct)}</span>
          <span style={s.valueLabel}>Rate</span>
        </div>
      ) : null}
    </div>
  );
}

// ─── Progress bar ──────────────────────────────────────────────

function ProgressBar({ pct }: { pct: number | null }): JSX.Element | null {
  if (pct === null) return null;
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <div style={s.progressRow}>
      <div style={s.progressBarOuter}>
        <div style={{ ...s.progressBarInner, width: `${String(clamped)}%` }} />
      </div>
      <span style={s.progressValue}>{fmtPct(pct)}</span>
    </div>
  );
}

// ─── Tags ──────────────────────────────────────────────────────

function Tags({ tags }: { tags: readonly string[] }): JSX.Element | null {
  if (tags.length === 0) return null;
  return (
    <div style={s.tagRow}>
      {tags.map((t) => (
        <span key={t} style={s.tagPill}>{t}</span>
      ))}
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────

export interface BoardItemCardProps {
  item: BoardItem;
  selected?: boolean;
  onClick?: (item: BoardItem) => void;
}

export function BoardItemCard({ item, selected, onClick }: BoardItemCardProps): JSX.Element {
  const stripe = PRIORITY_STRIPE[item.priority] ?? s.priorityNone;
  const cardBase = selected ? s.itemCardSelected : s.itemCard;

  return (
    <div
      style={{ ...cardBase, ...stripe }}
      onClick={onClick ? () => onClick(item) : undefined}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {/* Header: title + status */}
      <div style={s.itemHeaderRow}>
        <div style={{ display: "flex", flexDirection: "column", gap: "2px", flex: 1 }}>
          <span style={s.itemTitle}>{item.title}</span>
          {item.subtitle !== null ? (
            <span style={s.itemSubtitle}>{item.subtitle}</span>
          ) : null}
        </div>
        <span style={STATUS_STYLE[item.status]}>
          {STATUS_LABELS[item.status]}
        </span>
      </div>

      {/* Priority badge (only for non-none) */}
      {item.priority !== "none" ? (
        <span style={PRIORITY_TEXT[item.priority]}>
          {PRIORITY_LABELS[item.priority]}
        </span>
      ) : null}

      {/* Values */}
      <ValueBlock item={item} />

      {/* Progress */}
      <ProgressBar pct={item.value_pct} />

      {/* Tags */}
      <Tags tags={item.tags} />

      {/* Meta row: market, fixture, age */}
      <div style={s.metaRow}>
        <span style={s.metaItem}>{item.market_type}</span>
        {item.signal_type !== null ? (
          <>
            <span style={s.metaDot} />
            <span style={s.metaItem}>{item.signal_type}</span>
          </>
        ) : null}
        {item.fixture_id !== null ? (
          <>
            <span style={s.metaDot} />
            <span style={s.metaItem}>{item.fixture_id}</span>
          </>
        ) : null}
        <span style={s.metaDot} />
        <span style={s.metaItem}>{relativeTime(item.created_at)}</span>
      </div>

      {/* Approval indicator */}
      {item.requires_approval && item.status === "in_review" ? (
        <div style={s.approvalBanner}>
          <span style={s.approvalText}>Awaiting approval</span>
        </div>
      ) : null}
    </div>
  );
}
